/**
 * AddConversationDialog - Dialog para adicionar conversas a uma fase do Kanban
 * 
 * Permite buscar e selecionar conversas que ainda não estão vinculadas
 * a nenhuma fase para adicioná-las ao Kanban
 */

import { useState, useEffect } from 'react';
import { Search, MessageSquare, Check } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface Conversation {
  id: string;
  status: string;
  last_message_at: string | null;
  last_message_preview: string | null;
  contact: {
    id: string;
    name: string | null;
    phone: string | null;
    avatar_url: string | null;
  } | null;
  inbox: {
    id: string;
    name: string;
  } | null;
}

interface AddConversationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  phaseId: string;
  phaseName: string;
  onConversationsAdded: () => void;
}

export function AddConversationDialog({
  open,
  onOpenChange,
  phaseId,
  phaseName,
  onConversationsAdded,
}: AddConversationDialogProps) {
  const { currentTenant } = useAuth();
  const { toast } = useToast();
  
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Busca conversas sem fase do kanban
  useEffect(() => {
    if (!open || !currentTenant?.id) return;
    
    const fetchConversations = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('conversations')
          .select(`
            id,
            status,
            last_message_at,
            last_message_preview,
            contact:contacts(id, name, phone, avatar_url),
            inbox:inboxes(id, name)
          `)
          .is('kanban_phase_id', null)
          .eq('inbox.tenant_id', currentTenant.id)
          .not('contact_id', 'is', null)
          .order('last_message_at', { ascending: false })
          .limit(100);

        if (error) throw error;

        // Normaliza dados
        const normalized = (data || []).map(conv => ({
          ...conv,
          contact: Array.isArray(conv.contact) ? conv.contact[0] : conv.contact,
          inbox: Array.isArray(conv.inbox) ? conv.inbox[0] : conv.inbox,
        })).filter(conv => conv.inbox !== null);

        setConversations(normalized);
      } catch (error) {
        logger.error('[AddConversationDialog] Erro ao buscar conversas:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchConversations();
    setSelectedIds(new Set());
    setSearch('');
  }, [open, currentTenant?.id]);

  // Filtra conversas pela busca
  const filteredConversations = conversations.filter(conv => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    const name = conv.contact?.name?.toLowerCase() || '';
    const phone = conv.contact?.phone || '';
    const preview = conv.last_message_preview?.toLowerCase() || '';
    return name.includes(searchLower) || phone.includes(search) || preview.includes(searchLower);
  });

  // Toggle seleção
  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Seleciona/deseleciona todos
  const toggleAll = () => {
    if (selectedIds.size === filteredConversations.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredConversations.map(c => c.id)));
    }
  };

  // Adiciona conversas à fase
  const handleAdd = async () => {
    if (selectedIds.size === 0) return;
    
    setSaving(true);
    try {
      const { error } = await supabase
        .from('conversations')
        .update({ kanban_phase_id: phaseId })
        .in('id', Array.from(selectedIds));

      if (error) throw error;

      toast({
        title: 'Conversas adicionadas',
        description: `${selectedIds.size} conversa(s) adicionada(s) à fase "${phaseName}".`,
      });

      onConversationsAdded();
      onOpenChange(false);
    } catch (error) {
      logger.error('[AddConversationDialog] Erro ao adicionar conversas:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível adicionar as conversas.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Extrai iniciais
  const getInitials = (conv: Conversation) => {
    const name = conv.contact?.name || conv.contact?.phone || '?';
    if (/^\d+$/.test(name.replace(/\D/g, ''))) {
      return name.substring(0, 2);
    }
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Adicionar Conversas à Fase "{phaseName}"</DialogTitle>
        </DialogHeader>

        {/* Barra de Busca */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome, telefone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Lista de Conversas */}
        <ScrollArea className="h-[350px] -mx-6 px-6">
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <MessageSquare className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p>Nenhuma conversa disponível</p>
              <p className="text-sm">Todas as conversas já estão em alguma fase</p>
            </div>
          ) : (
            <div className="space-y-1">
              {/* Header com selecionar todos */}
              <div className="flex items-center gap-2 py-2 px-1 border-b">
                <Checkbox
                  checked={selectedIds.size === filteredConversations.length && filteredConversations.length > 0}
                  onCheckedChange={toggleAll}
                />
                <span className="text-sm text-muted-foreground">
                  {selectedIds.size > 0 
                    ? `${selectedIds.size} selecionada(s)` 
                    : 'Selecionar todas'}
                </span>
              </div>

              {/* Lista */}
              {filteredConversations.map(conv => {
                const isSelected = selectedIds.has(conv.id);
                const displayName = conv.contact?.name || conv.contact?.phone || 'Contato';
                const timeAgo = conv.last_message_at
                  ? formatDistanceToNow(new Date(conv.last_message_at), { addSuffix: true, locale: ptBR })
                  : null;

                return (
                  <div
                    key={conv.id}
                    className={cn(
                      'flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors',
                      isSelected ? 'bg-primary/10' : 'hover:bg-muted/50'
                    )}
                    onClick={() => toggleSelection(conv.id)}
                  >
                    <Checkbox checked={isSelected} />
                    
                    <Avatar className="h-9 w-9 shrink-0">
                      <AvatarImage src={conv.contact?.avatar_url || undefined} />
                      <AvatarFallback className="bg-primary/10 text-primary text-xs">
                        {getInitials(conv)}
                      </AvatarFallback>
                    </Avatar>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-sm truncate">{displayName}</span>
                        {timeAgo && (
                          <span className="text-[10px] text-muted-foreground shrink-0">
                            {timeAgo}
                          </span>
                        )}
                      </div>
                      {conv.last_message_preview && (
                        <p className="text-xs text-muted-foreground truncate">
                          {conv.last_message_preview}
                        </p>
                      )}
                      {conv.inbox?.name && (
                        <Badge variant="outline" className="h-4 text-[10px] px-1.5 mt-1">
                          {conv.inbox.name}
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleAdd} 
            disabled={selectedIds.size === 0 || saving}
          >
            {saving ? 'Adicionando...' : `Adicionar ${selectedIds.size > 0 ? `(${selectedIds.size})` : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
